import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function AddGallery() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: "",
    images: [],
    category: "other",
    status: "active",
  });

  const [loading, setLoading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const fileInputRef = useRef(null);

  // Image validation function
  const validateImage = useCallback((file) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        message: `${file.name}: Invalid file type. Please select JPEG, PNG, or WebP images.`
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        message: `${file.name}: File size too large. Maximum size is 2MB.`
      };
    }

    return { valid: true };
  }, []);

  // Auto-upload single image with progress tracking
  const uploadSingleImage = useCallback(async (imageFile, index) => {
    const uploadId = `${index}-${Date.now()}`;

    setUploadingImages(prev => [...prev, uploadId]);

    const imageFormData = new FormData();
    imageFormData.append("file", imageFile);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          [uploadId]: Math.min((prev[uploadId] || 0) + 10, 90)
        }));
      }, 200);

      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", imageFormData);

      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [uploadId]: 100 }));

      if (response?.success) {
        const imageUrl = response?.url || response.data?.imageUrl || response.data?.filePath;

        setUploadedImages(prev => [...prev, {
          index,
          url: imageUrl,
          file: imageFile,
          uploadId
        }]);

        toast.success(`${imageFile.name} uploaded successfully!`);
        return imageUrl;
      } else {
        throw new Error(response?.message || `Failed to upload ${imageFile.name}`);
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(error.response?.data?.message || `Error uploading ${imageFile.name}`);
      return null;
    } finally {
      setTimeout(() => {
        setUploadingImages(prev => prev.filter(id => id !== uploadId));
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[uploadId];
          return newProgress;
        });
      }, 1000);
    }
  }, []);

  // Process and validate multiple images
  const processImageFiles = useCallback(async (fileArray) => {
    // Validate all images first
    const validationResults = fileArray.map(file => ({
      file,
      validation: validateImage(file)
    }));

    const invalidFiles = validationResults.filter(result => !result.validation.valid);
    const validFiles = validationResults.filter(result => result.validation.valid);

    // Show errors for invalid files
    invalidFiles.forEach(result => {
      toast.error(result.validation.message);
    });

    if (validFiles.length === 0) return;

    const newFiles = validFiles.map(result => result.file);

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newFiles]
    }));

    // Create previews for new images
    const previewPromises = newFiles.map((file, offset) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve({
          url: reader.result,
          type: 'new',
          index: formData.images.length + offset,
          file
        });
        reader.readAsDataURL(file);
      });
    });

    try {
      const newPreviews = await Promise.all(previewPromises);
      setImagePreviews(prev => [...prev, ...newPreviews]);

      // Auto-upload all valid images with concurrency control
      const maxConcurrentUploads = 3;
      const uploadInBatches = async (files, startIndex) => {
        for (let i = 0; i < files.length; i += maxConcurrentUploads) {
          const batch = files.slice(i, i + maxConcurrentUploads);
          await Promise.all(
            batch.map((file, batchIndex) =>
              uploadSingleImage(file, startIndex + i + batchIndex)
            )
          );
        }
      };

      uploadInBatches(newFiles, formData.images.length);
    } catch (error) {
      console.error('Error creating previews:', error);
      toast.error('Error processing images');
    }
  }, [formData.images.length, validateImage, uploadSingleImage]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "images" && files && files.length > 0) {
      const fileArray = Array.from(files);
      processImageFiles(fileArray);

      // Reset file input to allow selecting same files again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const removeImage = useCallback((index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter(preview => preview.index !== index);

    // Remove from uploading and uploaded states
    const uploadIdToRemove = uploadedImages.find(img => img.index === index)?.uploadId;
    const newUploading = uploadingImages.filter(id => id !== uploadIdToRemove);
    const newUploaded = uploadedImages.filter(img => img.index !== index);

    setFormData(prev => ({ ...prev, images: newImages }));
    setImagePreviews(newPreviews);
    setUploadingImages(newUploading);
    setUploadedImages(newUploaded);

    // Clean up progress
    if (uploadIdToRemove) {
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[uploadIdToRemove];
        return newProgress;
      });
    }

    toast.info("Image removed");
  }, [formData.images, imagePreviews, uploadingImages, uploadedImages]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    if (formData.images.length === 0) {
      toast.error("Please select at least one image");
      return;
    }

    // Check if any images are still uploading
    if (uploadingImages.length > 0) {
      toast.info("Please wait for all images to finish uploading");
      return;
    }

    // Check if all selected images have been uploaded
    if (uploadedImages.length !== formData.images.length) {
      toast.error("Some images are still processing. Please wait or re-upload failed images.");
      return;
    }

    setLoading(true);

    try {
      const uploadedUrls = uploadedImages.map(img => img.url).filter(url => url);

      if (uploadedUrls.length === 0) {
        toast.error("No images were successfully uploaded. Please try again.");
        return;
      }

      const payload = {
        title: formData.title.trim(),
        images: uploadedUrls,
        category: formData.category,
        status: formData.status,
      };

      const response = await postData("api/gallery/create", payload);

      if (response) {
        const isSuccess = response.success ||
          response.status === "success" ||
          response.message?.toLowerCase().includes("success") ||
          response.message?.toLowerCase().includes("created");

        if (isSuccess) {
          toast.success(response.message || "Gallery item created successfully!");
          setTimeout(() => navigate("/gallery-list"), 1500);
        } else {
          throw new Error(response.message || "Failed to create gallery item");
        }
      } else {
        throw new Error("No response received from server");
      }
    } catch (error) {
      console.error("Add gallery error:", error);
      toast.error(error.response?.data?.message || error.message || "Error creating gallery item");
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: "event", label: "Event" },
    { value: "team", label: "Team" },
    { value: "office", label: "Office" },
    { value: "achievement", label: "Achievement" },
    { value: "other", label: "Other" },
  ];

  // Calculate upload statistics
  const totalImages = formData.images.length;
  const uploadedCount = uploadedImages.length;
  const uploadingCount = uploadingImages.length;
  const pendingCount = totalImages - uploadedCount - uploadingCount;

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Gallery Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Gallery</li>
                  <li className="breadcrumb-item active">Add Gallery</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/gallery-list")}
                disabled={loading}
              >
                <i className="fas fa-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Add New Gallery Item</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Title */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="title" className="form-label fw-medium">
                          Title <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="form-control"
                          placeholder="Enter gallery title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          minLength={3}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Category */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="category" className="form-label fw-medium">
                          Category <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="category"
                          name="category"
                          className="form-control"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                        >
                          {categoryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="images" className="form-label fw-medium">
                          Gallery Images <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          ref={fileInputRef}
                          type="file"
                          id="images"
                          name="images"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/jpeg, image/jpg, image/png, image/webp"
                          multiple
                          required={formData.images.length === 0}
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 800x600px, Max 2MB per image (JPEG/PNG/WebP). You can select multiple images.
                        </small>

                        {/* Upload Status Summary */}
                        {formData.images.length > 0 && (
                          <div className="mt-3 p-3 bg-light rounded">
                            <div className="d-flex flex-wrap gap-3 align-items-center">
                              <small className="text-info fw-medium">
                                Total: {totalImages} images selected
                              </small>
                              {uploadedCount > 0 && (
                                <small className="text-success">
                                  • {uploadedCount} uploaded
                                </small>
                              )}
                              {uploadingCount > 0 && (
                                <small className="text-warning">
                                  • {uploadingCount} uploading
                                </small>
                              )}
                              {pendingCount > 0 && (
                                <small className="text-secondary">
                                  • {pendingCount} pending
                                </small>
                              )}
                            </div>
                            {uploadingCount > 0 && (
                              <div className="mt-2">
                                <div className="progress" style={{ height: '6px' }}>
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{
                                      width: `${(uploadedCount / totalImages) * 100}%`
                                    }}
                                    aria-valuenow={(uploadedCount / totalImages) * 100}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                  </div>
                                </div>
                                <small className="text-muted">
                                  {uploadedCount} of {totalImages} images uploaded
                                </small>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Image Previews */}
                        {imagePreviews.length > 0 && (
                          <div className="mt-4">
                            <h6 className="text-success mb-3">
                              <i className="fas fa-images me-2"></i>
                              Selected Images ({imagePreviews.length})
                            </h6>
                            <div className="d-flex flex-wrap gap-3 justify-content-start">
                              {imagePreviews.map((preview) => {
                                const isUploading = uploadingImages.some(id =>
                                  id.startsWith(`${preview.index}-`)
                                );
                                const isUploaded = uploadedImages.some(img => img.index === preview.index);
                                const uploadId = uploadedImages.find(img => img.index === preview.index)?.uploadId;
                                const progress = uploadProgress[uploadId] || 0;

                                return (
                                  <ImagePreview
                                    key={`preview-${preview.index}`}
                                    preview={preview}
                                    onRemove={() => removeImage(preview.index)}
                                    isUploading={isUploading}
                                    isUploaded={isUploaded}
                                    progress={progress}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="status"
                            checked={formData.status === "active"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                status: e.target.checked ? "active" : "inactive",
                              })
                            }
                            role="switch"
                            style={{ width: "2.5em", height: "1.25em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="status">
                            {formData.status === "active" ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-muted">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2 px-4"
                          disabled={loading || uploadingImages.length > 0 || uploadedImages.length !== formData.images.length}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Creating...
                            </>
                          ) : (
                            "Create Gallery"
                          )}
                        </button>

                        {(uploadingImages.length > 0 || uploadedImages.length !== formData.images.length) && (
                          <div className="mt-2">
                            <small className="text-warning">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Please wait for all images to upload before submitting
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Separate component for image preview for better performance
const ImagePreview = React.memo(({
  preview,
  onRemove,
  isUploading = false,
  isUploaded = false,
  progress = 0
}) => {
  return (
    <div className="image-preview-container position-relative" style={{ flex: '0 0 auto' }}>
      <div className="position-relative">
        <img
          src={preview.url}
          alt={`Preview ${preview.index + 1}`}
          className={`img-thumbnail ${isUploaded ? 'border-success border-2' :
              isUploading ? 'border-warning border-2' : 'border-secondary border-2'
            }`}
          style={{
            width: "120px",
            height: "80px",
            objectFit: "cover",
            filter: isUploading ? 'brightness(0.8)' : 'none',
            transition: 'all 0.2s ease'
          }}
        />

        {/* Upload Progress */}
        {isUploading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded">
            <div className="text-center">
              <div className="spinner-border text-white spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <small className="text-white d-block mt-1">{progress}%</small>
            </div>
          </div>
        )}

        {/* Upload Success Indicator */}
        {isUploaded && (
          <div className="position-absolute top-0 end-0 bg-success text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: '20px', height: '20px', fontSize: '12px', fontWeight: 'bold' }}>
            ✓
          </div>
        )}
      </div>

      {/* Remove Button */}
      <button
        type="button"
        className="btn btn-danger btn-sm position-absolute top-0 start-0 mt-1 ms-1"
        style={{
          padding: "1px 4px",
          fontSize: "10px",
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onRemove}
        title="Remove this image"
        disabled={isUploading}
      >
        ×
      </button>

      <small className={`text-muted d-block text-center mt-1 ${isUploading ? 'text-warning' :
          isUploaded ? 'text-success' : 'text-secondary'
        }`} style={{ fontSize: '0.75rem' }}>
        {isUploading ? 'Uploading...' :
          isUploaded ? 'Uploaded' : 'Pending'} {preview.index + 1}
      </small>
    </div>
  );
});